import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { Feature } from "geojson";
import { enqueueSnackbar } from "notistack";

import { selectLayerByVersionAndLayerKey } from "fond/api";
import { HistoryContext, useHistory } from "fond/history";
import MapOverlay from "fond/map/MapOverlay";
import { MapContext } from "fond/map/MapProvider";
import { useAppSelector } from "fond/utils/hooks";
import { MapOverlayCloseButton } from "fond/widgets";

import { AttributeField, mergeLayerAttributes } from "./attributeHelpers";
import FeatureAttributesForm from "./FeatureAttributesForm";

export interface AttributeFormPopupHandle {
  /**
   * Show the attribute form popup for a feature if the layer has PromptAttributesOnCreate enabled
   * @param featureId The ID of the feature to show attributes for
   */
  showForFeature: (featureId: string) => void;
}

/**
 * Overlay form for entering attribute values when a feature is created.
 * Exposes a showForFeature method that DrawHandler calls after creating a feature.
 */
const AttributeFormPopup = forwardRef<AttributeFormPopupHandle>((_, ref) => {
  const { drawControl } = useContext(MapContext);
  const { set: setHistory } = useHistory(HistoryContext);
  const versionId = useAppSelector((state) => state.project.versionId);

  const [popup, setPopup] = useState<{
    feature: Feature;
    attributes: AttributeField[];
  } | null>(null);

  // Get editingLayerId from drawControl options
  const editingLayerId = drawControl.current?.options?.editOptions?.editingLayerId;

  // Get the specific layer's attributes based on editingLayerId
  const layerAttributes = useAppSelector((state) =>
    editingLayerId ? selectLayerByVersionAndLayerKey(state, { versionId, layerId: editingLayerId })?.Attributes : undefined
  );

  useImperativeHandle(ref, () => ({
    showForFeature: (featureId: string) => {
      // Read current values from drawControl
      const currentEditingLayerId = drawControl.current?.options?.editOptions?.editingLayerId;
      const currentEditableLayers = drawControl.current?.options?.editOptions?.allLayers;

      // Find the current layer configuration
      const currentLayer = currentEditableLayers?.find((layer) => layer.ID === currentEditingLayerId);

      // Check if we should prompt for attributes
      if (!currentLayer?.PromptAttributesOnCreate) {
        return;
      }

      const freshFeature = drawControl.current?.get(featureId) as Feature;
      if (!freshFeature) return;

      // Merge attributes from EditingLayer with layer schema
      const attributesArray = mergeLayerAttributes(layerAttributes, currentLayer);

      // Only show popup if we have attributes to display
      if (attributesArray.length === 0) return;

      setPopup({
        feature: freshFeature,
        attributes: attributesArray,
      });
    },
  }));

  const handleSubmit = useCallback(
    (_values: Record<string, string | number | boolean>) => {
      // Update history silently after attribute values are set
      if (drawControl.current?.getAll()) {
        setHistory(drawControl.current.getAll(), { silent: true });
      }
      setPopup(null);
      enqueueSnackbar("Feature attributes updated", { variant: "success" });
    },
    [drawControl, setHistory]
  );

  const handleClose = useCallback(() => {
    setPopup(null);
  }, []);

  // Close modal on Escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && popup) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [popup, handleClose]);

  if (!popup) {
    return null;
  }

  return (
    <MapOverlay>
      <Paper sx={{ minWidth: 400, maxWidth: 600, position: "relative" }}>
        <MapOverlayCloseButton onClose={handleClose} />
        <Box sx={{ p: 2, pt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Feature Attributes
          </Typography>
          <FeatureAttributesForm feature={popup.feature} attributes={popup.attributes} onSubmit={handleSubmit} onCancel={handleClose} />
        </Box>
      </Paper>
    </MapOverlay>
  );
});

AttributeFormPopup.displayName = "AttributeFormPopup";

export default AttributeFormPopup;
